class TexanArticleRelatedProducts extends HTMLElement {
  connectedCallback() {
    this.minCount = parseInt(this.dataset.minCount || '4', 10);
    this.make = (this.dataset.make || '').trim().toLowerCase();
    this.model = (this.dataset.model || '').trim().toLowerCase();
    this.brand = (this.dataset.brand || '').trim().toLowerCase();
    this.searchQuery = (this.dataset.query || '').trim();
    this.indexUrl = this.dataset.indexUrl || '';
    this.handlesSectionId = this.dataset.handlesSectionId || 'article-product-handles';
    this.searchSectionId = this.dataset.searchSectionId || 'article-search-products';

    this.run();
  }

  async run() {
    const target = this.querySelector('.article-related-products__grid');
    if (!target) return;

    const seen = new Set(
      Array.from(target.querySelectorAll('[data-product-id]')).map((el) => el.dataset.productId)
    );

    try {
      const indexHandles = await this.loadFitmentHandles();
      if (indexHandles.length) {
        target.innerHTML = '';
        seen.clear();
        await this.appendHandlesSection(target, indexHandles, seen);
      }
    } catch (_) {
      /* fitment index optional */
    }

    if (this.gridCount(target) < this.minCount && this.searchQuery) {
      await this.appendSearchSection(target, this.searchQuery, seen);
    }

    this.classList.remove('article-related-products--loading');
  }

  gridCount(target) {
    return target.querySelectorAll('.grid__item').length;
  }

  normalizeVehicleKey(make, model) {
    const mk = make.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const md = model.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    if (!mk || !md) return '';
    return `${mk}|${md}`;
  }

  vehicleLookupKeys(make, model) {
    const keys = new Set();
    const primary = this.normalizeVehicleKey(make, model);
    if (primary) keys.add(primary);
    if (make.toLowerCase() === 'chevrolet') keys.add(this.normalizeVehicleKey('chevy', model));
    if (make.toLowerCase() === 'chevy') keys.add(this.normalizeVehicleKey('chevrolet', model));
    const compactModel = model.replace(/\s+/g, '');
    if (compactModel && compactModel !== model) {
      keys.add(this.normalizeVehicleKey(make, compactModel));
    }
    return Array.from(keys).filter(Boolean);
  }

  async loadFitmentHandles() {
    if (!this.indexUrl || !this.make || !this.model) return [];

    const response = await fetch(this.indexUrl, { credentials: 'same-origin' });
    if (!response.ok) return [];
    const payload = await response.json();
    const index = payload.index || {};
    const handles = [];

    for (const key of this.vehicleLookupKeys(this.make, this.model)) {
      for (const handle of index[key] || []) {
        if (!handle || handles.includes(handle)) continue;
        handles.push(handle);
        if (handles.length >= this.minCount) return handles;
      }
    }
    return handles;
  }

  async appendHandlesSection(target, handles, seen) {
    const needed = handles.filter((h) => h && !seen.has(h)).slice(0, this.minCount);
    if (!needed.length) return;

    const basePath = window.location.pathname;
    const url = `${basePath}?section_id=${encodeURIComponent(this.handlesSectionId)}&handles=${encodeURIComponent(needed.join(','))}`;
    const html = await this.fetchSectionHtml(url);
    this.mergeGridItems(target, html, '.article-product-handles__results .grid__item', seen);
  }

  async appendSearchSection(target, query, seen) {
    if (this.gridCount(target) >= this.minCount) return;
    const searchUrl = `${window.routes?.search_url || '/search'}?q=${encodeURIComponent(query)}&type=product&section_id=${encodeURIComponent(this.searchSectionId)}`;
    const html = await this.fetchSectionHtml(searchUrl);
    this.mergeGridItems(target, html, '.article-related-products__results .grid__item', seen);
  }

  async fetchSectionHtml(url) {
    const response = await fetch(url);
    if (!response.ok) return '';
    return response.text();
  }

  mergeGridItems(target, html, selector, seen) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const items = doc.querySelectorAll(selector);
    items.forEach((item) => {
      if (this.gridCount(target) >= this.minCount) return;
      const handle = item.dataset.productId;
      if (handle) {
        if (seen.has(handle)) return;
        seen.add(handle);
      } else {
        const link = item.querySelector('a[href*="/products/"]');
        const match = link?.getAttribute('href')?.match(/\/products\/([^/?#]+)/);
        const parsed = match ? match[1] : null;
        if (parsed) {
          if (seen.has(parsed)) return;
          seen.add(parsed);
          item.dataset.productId = parsed;
        }
      }
      target.appendChild(item);
    });
  }
}

if (!customElements.get('texan-article-related-products')) {
  customElements.define('texan-article-related-products', TexanArticleRelatedProducts);
}
